import axios from 'axios';
import { mapValues, sortBy, startCase } from 'lodash-es';
import { action, keys, observable, toJS } from 'mobx';
import { loaded } from './document-promises';
import { InitialUrlSearchParams, isProduction } from './environment';
import { isBrowserCompliantCurrencyCode, retryPromise } from './helpers';
import { ShippingCountryState } from './shipping';
import { notNil } from './typescriptUtils';
import { ALO_API_HOST } from './constants';
import { currentPathEquals } from './localization';

const LOCAL_STORAGE_KEY = 'alo-users-preferred-currency-v2'; // ? [REVISIT] Name must be updated once GE hosted implementatin is dropped;

const DEFAULT_CURRENCY_CODE = 'USD';
const USD_EXCHANGE_RATE = { USD: 1 } as const;
const DEFAULT_EXCHANGE_RATES: ValidExchangeRates = USD_EXCHANGE_RATE;

export const getUsersPreferredCurrency = () => localStorage.getItem(LOCAL_STORAGE_KEY);

const storedCurrencyPreference = getUsersPreferredCurrency();

const currencyStateDefinition = {
  // ? should we have an initial value vs undefined? check from the supported list
  // ? [REVISIT] sanatize once we have the full list
  currencyCode: storedCurrencyPreference || DEFAULT_CURRENCY_CODE,
  exchangeRates: DEFAULT_EXCHANGE_RATES,
  get currencyOptions() {
    return sortBy(
      keys(this.exchangeRates)
        .map((code) => {
          const countryCode = toJS(ShippingCountryState?.supportedCountries)?.find(
            (country) => country.currency.code.toLowerCase() === code.toString().toLowerCase()
          )?.countryCode;
          return typeof code === 'string' && countryCode
            ? {
                countryCode,
                currencyCode: code,
                currencyName: startCase(getCurrencyNameOrSymbol(code, 'name')),
                currencySymbol: getCurrencyNameOrSymbol(code, 'narrowSymbol'), // ? [REVISIT] vs 'symbol' check mdn
              }
            : null;
        })
        .filter(notNil),
      'currencyName'
    );
  },
};

const CurrencyState = observable(currencyStateDefinition, { exchangeRates: observable.shallow });

/**
 * returns `true` if successful
 */
export const changeCurrencyCode = action(
  (_currencyCode: string | undefined | null, { updateUserPreference = false } = {}) => {
    if (!_currencyCode) return false;
    const currentCountry = ShippingCountryState.countryCode || localStorage.getItem('alo-users-prefered-country');
    const currencyCode = _currencyCode?.toUpperCase();
    const isValid = currencyCode && isValidCurrencyCode(currencyCode);

    if (isValid) {
      window?.heap?.addUserProperties({
        'Shipping Country Code': currentCountry,
        'Preferred Currency': currencyCode,
      });
      CurrencyState.currencyCode = currencyCode;
      if (updateUserPreference) localStorage.setItem(LOCAL_STORAGE_KEY, currencyCode);
    } else {
      const action = isProduction ? console.error : console.warn;
      action(`Failed 'changeCurrencyCode' for '${currencyCode}'`);
    }
    return isValid;
  }
);

export const setExchangeRates = action((exchangeRates: ExchangeRates) => {
  const validEntries = Object.entries(exchangeRates).filter(([code]) => {
    return code.length === 3 && isBrowserCompliantCurrencyCode(code);
  });
  const validExchangeRates: ValidExchangeRates = { ...Object.fromEntries(validEntries), ...USD_EXCHANGE_RATE };
  CurrencyState.exchangeRates = validExchangeRates;
});

// Update currency/locale on browser tab switch
document.addEventListener('visibilitychange', (e) => {
  const currencyFromLS = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (document.visibilityState === 'visible') {
    changeCurrencyCode(currencyFromLS);
  }
});

const getExchangeRates = (): Promise<ExchangeRates> =>
  axios
    .get<ExchangeRatesResponse>(`${ALO_API_HOST}/v1/geoip/exchange`)
    .then(({ data }) => mapValues(data, (value) => Number(value)));

const updateInitialCurrencyStateConditionally = async () => {
  await loaded;
  const currentCountry = ShippingCountryState?.countryCode;
  const currentCurrency = ShippingCountryState?.supportedCountries?.find(
    (country) => country.countryCode === currentCountry?.toUpperCase()
  )?.currency.code;

  const countryFromURLparam = InitialUrlSearchParams.get('country') || InitialUrlSearchParams.get('setCountry');

  // Set exchange rates for Loyalty points calculation
  const isThankYouOrOrderStatusPage =
    window.Shopify?.Checkout?.page === 'thank_you' || window.Shopify?.Checkout?.isOrderStatusPage;

  const shouldFetchExchangeRatesForLoyalty =
    (currentPathEquals('/cart') || isThankYouOrOrderStatusPage) &&
    ShippingCountryState.countryCode?.toLowerCase() !== 'us';
  if (shouldFetchExchangeRatesForLoyalty) {
    await retryPromise(getExchangeRates, 3).then((exchangeRates) => {
      setExchangeRates(exchangeRates);
    });
  }

  if (countryFromURLparam) {
    const currency = ShippingCountryState?.supportedCountries?.find(
      (country) => country.countryCode === countryFromURLparam.toUpperCase()
    )?.currency.code;
    changeCurrencyCode(currency, { updateUserPreference: true });
  } else if (!storedCurrencyPreference || storedCurrencyPreference !== currentCurrency) {
    if (currentCurrency) changeCurrencyCode(currentCurrency, { updateUserPreference: true });
  }
};
updateInitialCurrencyStateConditionally();

export const toLocalCurrency = (input: number, formatOptions?: Intl.NumberFormatOptions | undefined): string => {
  // Explicitly set locale to 'en' instead of `navigator.language` since Global-E cannot stomach localized price
  const currency = CurrencyState.currencyCode;
  return input.toLocaleString('en', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
    minimumFractionDigits: Number.isInteger(input) ? 0 : 2,
    ...formatOptions,
  });
};

export const toLocalCurrencyShowEstimate = (
  input: number,
  formatOptions?: Intl.NumberFormatOptions | undefined
): string => {
  return toLocalCurrency(input, formatOptions);
};

export const formatToUSD = (input: number, formatOptions?: Intl.NumberFormatOptions | undefined) =>
  input.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    ...formatOptions,
  });

export { CurrencyState };

const isValidCurrencyCode = (currencyCode: string) => {
  return isBrowserCompliantCurrencyCode(currencyCode);
};

export const getCurrencyNameOrSymbol = (currency: string, input: 'name' | 'symbol' | 'narrowSymbol') => {
  try {
    const formatter = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      currencyDisplay: input,
    });
    const parts = formatter.formatToParts(0);
    return parts.find((item) => item.type === 'currency')!.value;
  } catch (err) {
    // ? [REVISIT] what do?
    return '';
  }
};

type ExchangeRates = Record<string, number>;
type ValidExchangeRates = ExchangeRates & typeof USD_EXCHANGE_RATE;

interface ExchangeRatesResponse {
  [key: string]: string;
}
